/**
 * OperatorCard — single selectable operator card on the splash screen.
 *
 * Renders a deterministic gradient avatar (hash → hue), the display name,
 * and the consumer_id (revealed on hover/focus/selected). Animates lift on
 * hover; border + glow on selection.
 */

import type { CSSProperties, KeyboardEvent } from 'react'
import { useMemo } from 'react'
import type { Operator } from '@/lib/tauri'

interface Props {
  operator: Operator
  isSelected: boolean
  /** True if any operator in the radio group is currently selected. Drives the
   * "fade unselected siblings" behaviour described in the task spec. */
  isAnySelected: boolean
  onSelect: () => void
  onActivate: () => void
  /** Forward keyboard navigation to the parent for arrow-key handling. */
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void
  innerRef: (el: HTMLButtonElement | null) => void
  /** Fired when the pointer enters the card or it gains focus. Used by the
   *  splash background to spell out the hovered operator's initials. */
  onHoverStart?: () => void
  /** Fired when the pointer leaves the card or it loses focus. */
  onHoverEnd?: () => void
  /**
   * Drives the card's entrance fade. False = invisible (pre-mount), true =
   * fade up to full opacity. We keep this on the *card itself* rather than a
   * parent so the card never has an opacity-bearing ancestor — which would
   * force the browser to render the subtree to an offscreen group and break
   * `backdrop-filter` compositing for the duration of the fade. Defaults to
   * true so loading/error states (which still use a parent fade) don't have
   * to opt in.
   */
  hasMounted?: boolean
}

/**
 * Generate a deterministic HSL gradient pair from a string seed.
 * Same input always produces the same gradient — operators get a stable
 * visual identity across launches.
 */
function gradientFor(seed: string): { from: string; to: string } {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0 // 32-bit
  }
  const hue = Math.abs(hash) % 360
  // Single-hue gradient: deeper shade → lighter shade of the same color.
  return {
    from: `hsl(${hue}, 72%, 42%)`,
    to: `hsl(${hue}, 65%, 62%)`,
  }
}

export function OperatorCard({
  operator,
  isSelected,
  isAnySelected,
  onSelect,
  onActivate,
  onKeyDown,
  innerRef,
  onHoverStart,
  onHoverEnd,
  hasMounted = true,
}: Props) {
  const gradient = useMemo(
    () => gradientFor(operator.consumer_id),
    [operator.consumer_id],
  )

  // Resting opacity = the existing "fade unselected siblings" rule. Pre-mount
  // we override to 0 so the card fades up from invisible. The transition on
  // .card animates this — see styles.card.transition (opacity 360ms).
  const restingOpacity = isAnySelected && !isSelected ? 0.5 : 1
  const cardStyle: CSSProperties = {
    ...styles.card,
    borderColor: isSelected
      ? 'rgba(59, 130, 246, 0.6)'
      : 'rgba(255, 255, 255, 0.1)',
    boxShadow: isSelected
      ? '0 12px 32px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(59, 130, 246, 0.6), 0 0 48px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
      : '0 4px 16px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    opacity: hasMounted ? restingOpacity : 0,
    transform: isSelected ? 'translateY(-6px) scale(1.02)' : 'translateY(0) scale(1)',
    background: isSelected
      ? 'rgba(59, 130, 246, 0.08)'
      : 'rgba(255, 255, 255, 0.04)',
  }

  const avatarStyle: CSSProperties = {
    ...styles.avatar,
    background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
  }

  const idStyle: CSSProperties = {
    ...styles.consumerId,
    opacity: isSelected ? 1 : 0,
  }

  return (
    <button
      ref={innerRef}
      type="button"
      role="radio"
      aria-checked={isSelected}
      aria-label={`Sign in as ${operator.display_name}`}
      className="operator-selector-card"
      style={cardStyle}
      onClick={(e) => {
        // Single click selects. Double-click acts as activate (continue).
        if (e.detail >= 2) {
          onActivate()
        } else {
          onSelect()
        }
      }}
      onKeyDown={onKeyDown}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onFocus={onHoverStart}
      onBlur={onHoverEnd}
      data-testid={`operator-selector-card-${operator.consumer_id}`}
    >
      <div style={avatarStyle} aria-hidden>
        <span style={styles.initials}>{operator.initials}</span>
      </div>
      <div style={styles.name}>{operator.display_name}</div>
      <div style={idStyle} className="mono">
        {operator.consumer_id}
      </div>
      {operator.consumer_id === 'con_stream_mjt' && (
        <div style={styles.legacyBadge}>Legacy</div>
      )}
    </button>
  )
}

const styles: Record<string, CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '14px',
    width: '180px',
    height: '220px',
    padding: '24px 16px 20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 'var(--radius-lg)',
    background: 'rgba(255, 255, 255, 0.04)',
    // Lightweight frosted-glass: blur is small enough that individual
    // particles behind the card stay recognisable rather than smearing into
    // a uniform haze. With the previous blur(12px) the card looked solid
    // against the particle field once the entrance fade finished, which
    // read as "the cards jump to opaque".
    backdropFilter: 'blur(4px) saturate(110%)',
    WebkitBackdropFilter: 'blur(4px) saturate(110%)',
    cursor: 'pointer',
    // Opacity uses the longer 360ms duration so the entrance fade has a
    // chance to read; transform/colour transitions stay snappy at 200ms so
    // hover and select still feel responsive.
    transition:
      'transform 200ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease-out, border-color 200ms ease-out, opacity 360ms ease-out, background 200ms ease-out',
    fontFamily: 'inherit',
  },
  avatar: {
    display: 'flex',
    width: '80px',
    height: '80px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow:
      'inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  initials: {
    fontSize: '26px',
    fontWeight: 600,
    color: 'white',
    letterSpacing: '0.02em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
  },
  name: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    textAlign: 'center',
    letterSpacing: '-0.01em',
    margin: 0,
  },
  consumerId: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    transition: 'opacity 180ms ease-out',
    margin: 0,
  },
  legacyBadge: {
    marginTop: 'auto',
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.28)',
  },
}
